import React, { useState, useEffect } from 'react';
import { CreditCard, Clock, Calendar, ShieldAlert, ArrowRight, Loader2, AlertCircle, UploadCloud, CheckCircle } from 'lucide-react';
import Swal from 'sweetalert2';
import { getPaymentInfo, DEFAULT_PAYMENT_DEADLINE_DISPLAY } from '../../utils/paymentUtils';
import type { PaymentInfo } from '../../utils/paymentUtils';

interface PaymentTrackerCardProps {
    user: any;
    cohort: any;
    onPaymentSuccess?: () => void;
}

export const PaymentTrackerCard: React.FC<PaymentTrackerCardProps> = ({ user, cohort, onPaymentSuccess }) => {
    const [info, setInfo] = useState<PaymentInfo>(() => getPaymentInfo(user, cohort));
    const [isProcessing] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    // Live 1-second ticker update
    useEffect(() => {
        const timer = setInterval(() => {
            setInfo(getPaymentInfo(user, cohort));
        }, 1000);
        return () => clearInterval(timer);
    }, [user, cohort]);

    const handlePayBalance = async () => {
        const directSquareLink = info.paymentLink || 'https://square.link/u/HjJ7Mn7U';
        window.open(directSquareLink, '_blank');
    };

    const handleUploadReceipt = async () => {
        const { value: file } = await Swal.fire({
            title: 'Upload Proof of Payment',
            text: 'Please select your payment receipt, Square receipt, or transfer screenshot (JPEG, PNG, PDF up to 10MB).',
            input: 'file',
            inputAttributes: {
                accept: 'image/*,application/pdf',
                'aria-label': 'Upload payment proof'
            },
            showCancelButton: true,
            confirmButtonText: 'Submit Proof',
            cancelButtonText: 'Cancel',
            confirmButtonColor: 'var(--index-primary-color, #0F5132)'
        });

        if (!file) return;

        setIsUploading(true);
        const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
        const formData = new FormData();
        formData.append('receipt', file);

        try {
            const response = await fetch(`${API_URL}/student/upload-receipt`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: formData
            });

            const data = await response.json();
            if (response.ok && data.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Proof Submitted!',
                    text: data.message || 'Proof of payment uploaded successfully. The admissions team will verify your balance settlement shortly.',
                    confirmButtonColor: 'var(--index-primary-color, #0F5132)'
                });
                if (onPaymentSuccess) onPaymentSuccess();
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Upload Failed',
                    text: data.message || 'Could not process receipt upload. Please check the file format and try again.',
                    confirmButtonColor: '#dc2626'
                });
            }
        } catch (err: any) {
            Swal.fire({
                icon: 'error',
                title: 'Network Error',
                text: err.message || 'Error communicating with server.',
                confirmButtonColor: '#dc2626'
            });
        } finally {
            setIsUploading(false);
        }
    };

    // HIDE CARD COMPLETELY WHEN INACTIVE OR FULLY PAID
    if (info.isFullyPaid || info.bannerLevel === 'none') {
        return null;
    }

    const deadlineText = info.deadlineDisplay || DEFAULT_PAYMENT_DEADLINE_DISPLAY;

    // 2. NO ASSIGNED COHORT STATE
    if (!info.hasCohort) {
        return (
            <div className="bg-white dark:bg-brand-charcoal rounded-3xl p-6 sm:p-8 border-2 border-brand-border shadow-sm">
                <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-amber-500/10 text-amber-500 rounded-2xl flex items-center justify-center shrink-0 border border-amber-500/20">
                        <CreditCard size={24} />
                    </div>
                    <div className="space-y-1">
                        <h4 className="text-lg font-black text-brand-charcoal dark:text-white uppercase tracking-tight">Payment Access Tracker</h4>
                        <p className="text-xs text-brand-muted font-medium leading-relaxed">
                            You currently have no assigned cohort. Your 50% installment deadline is set to <strong className="text-brand-charcoal dark:text-white">{deadlineText}</strong>.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // 3. EXPIRED PAYMENT STATE
    if (info.isExpired) {
        return (
            <div className="bg-red-600 text-white rounded-3xl p-6 sm:p-8 border-2 border-red-700 shadow-xl space-y-6 animate-in zoom-in-95 duration-300">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 border-b border-white/20 pb-6">
                    <div className="flex items-start gap-4">
                        <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center shrink-0 text-white border border-white/30">
                            <ShieldAlert size={32} />
                        </div>
                        <div className="space-y-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="text-xl font-black uppercase tracking-tight">Access Suspended - Payment Deadline Passed</h3>
                                <span className="px-3 py-1 bg-white text-red-700 rounded-full text-[10px] font-black uppercase tracking-widest">
                                    Payment Overdue
                                </span>
                            </div>
                            <p className="text-xs font-medium opacity-90 leading-relaxed max-w-xl">
                                Your payment deadline (<span className="underline font-bold">{deadlineText}</span>) has expired. Access to courses and materials is suspended. Please settle your remaining balance of <span className="font-black text-white underline">${info.remainingBalance}</span> to immediately restore access.
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                        <button
                            type="button"
                            onClick={handlePayBalance}
                            disabled={isProcessing}
                            className="w-full sm:w-auto px-6 h-14 bg-white text-red-600 rounded-2xl font-black text-xs uppercase tracking-widest shadow-2xl hover:bg-slate-100 active:scale-95 transition-all border-none cursor-pointer flex items-center justify-center gap-3 shrink-0 disabled:opacity-50"
                        >
                            {isProcessing ? (
                                <><Loader2 size={18} className="animate-spin" /> Connecting Square...</>
                            ) : (
                                <><CreditCard size={18} /> Pay Remaining Balance (${info.remainingBalance}) <ArrowRight size={16} /></>
                            )}
                        </button>

                        <button
                            type="button"
                            onClick={handleUploadReceipt}
                            disabled={isUploading}
                            className="w-full sm:w-auto px-6 h-14 bg-red-700/80 hover:bg-red-700 text-white border border-white/30 rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-2 shrink-0 disabled:opacity-50"
                        >
                            {isUploading ? (
                                <><Loader2 size={16} className="animate-spin" /> Uploading...</>
                            ) : (
                                <><UploadCloud size={16} /> Upload Proof</>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // 4. ACTIVE COUNTDOWN STATE (50% Paid)
    return (
        <div className="bg-white dark:bg-brand-charcoal rounded-3xl p-6 sm:p-8 border-2 border-brand-border shadow-sm space-y-6">
            {/* Top Bar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-brand-border/60 pb-6">
                <div className="flex items-center gap-4 min-w-0">
                    <div className="w-12 h-12 bg-amber-500/10 text-amber-500 rounded-2xl flex items-center justify-center shrink-0 border border-amber-500/20">
                        <Clock size={24} />
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-xl font-black text-brand-charcoal dark:text-white uppercase tracking-tight">Access Revocation Countdown</h3>
                            <span className="px-3 py-0.5 bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 rounded-full text-[10px] font-black uppercase tracking-widest">
                                50% Balance Remaining: ${info.remainingBalance}
                            </span>
                        </div>
                        <p className="text-xs text-brand-muted font-medium mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                            <span>Cohort: <span className="font-bold text-brand-charcoal dark:text-white">{info.cohortName}</span></span>
                            <span className="hidden sm:inline text-brand-border">•</span>
                            <span>Course: <span className="font-bold text-brand-emerald">{info.courseTitle}</span></span>
                        </p>
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2.5 w-full sm:w-auto">
                    <button
                        type="button"
                        onClick={handlePayBalance}
                        disabled={isProcessing}
                        className="w-full sm:w-auto px-6 h-12 bg-brand-emerald text-white rounded-xl text-xs font-black uppercase tracking-widest shadow-lg shadow-brand-emerald/20 hover:scale-105 active:scale-95 transition-all border-none cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                        {isProcessing ? (
                            <><Loader2 size={16} className="animate-spin" /> Connecting Square...</>
                        ) : (
                            <><CreditCard size={16} /> Pay Balance (${info.remainingBalance}) <ArrowRight size={14} /></>
                        )}
                    </button>

                    <button
                        type="button"
                        onClick={handleUploadReceipt}
                        disabled={isUploading}
                        className="w-full sm:w-auto px-4 h-12 bg-brand-beige/60 dark:bg-white/10 hover:bg-brand-beige dark:hover:bg-white/15 text-brand-charcoal dark:text-white border border-brand-border rounded-xl text-xs font-black uppercase tracking-widest shadow-sm active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50"
                        title="Upload proof of payment / receipt after payment"
                    >
                        {isUploading ? (
                            <><Loader2 size={15} className="animate-spin" /> Submitting...</>
                        ) : (
                            <><UploadCloud size={15} /> Upload Proof</>
                        )}
                    </button>
                </div>
            </div>

            {/* Explanatory Deadline Notice */}
            <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-2xl flex items-center gap-3.5 text-xs text-amber-900 dark:text-amber-200">
                <AlertCircle size={20} className="text-amber-500 shrink-0" />
                <p className="font-medium leading-relaxed">
                    Access Revocation Deadline: <strong className="font-black underline">{deadlineText}</strong>. When this live countdown reaches zero, platform access will be suspended until your remaining 50% tuition balance (${info.remainingBalance}) is settled.
                </p>
            </div>

            {/* Countdown Grid & Metadata */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* Days */}
                <div className="p-4 bg-brand-beige/50 dark:bg-white/5 border border-brand-border/60 rounded-2xl text-center space-y-1">
                    <div className="text-3xl sm:text-4xl font-black text-brand-charcoal dark:text-white font-mono">{String(info.daysRemaining).padStart(2, '0')}</div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-brand-muted">Days</div>
                </div>
                {/* Hours */}
                <div className="p-4 bg-brand-beige/50 dark:bg-white/5 border border-brand-border/60 rounded-2xl text-center space-y-1">
                    <div className="text-3xl sm:text-4xl font-black text-brand-charcoal dark:text-white font-mono">{String(info.hoursRemaining).padStart(2, '0')}</div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-brand-muted">Hours</div>
                </div>
                {/* Minutes */}
                <div className="p-4 bg-brand-beige/50 dark:bg-white/5 border border-brand-border/60 rounded-2xl text-center space-y-1">
                    <div className="text-3xl sm:text-4xl font-black text-brand-charcoal dark:text-white font-mono">{String(info.minutesRemaining).padStart(2, '0')}</div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-brand-muted">Minutes</div>
                </div>
                {/* Seconds */}
                <div className="p-4 bg-brand-beige/50 dark:bg-white/5 border border-brand-border/60 rounded-2xl text-center space-y-1">
                    <div className="text-3xl sm:text-4xl font-black text-brand-emerald font-mono animate-pulse">{String(info.secondsRemaining).padStart(2, '0')}</div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-brand-muted">Seconds</div>
                </div>
            </div>

            {/* Details Footer */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between text-xs font-medium text-brand-muted gap-2 pt-2 border-t border-brand-border/60">
                <span className="flex items-center gap-1.5">
                    <Calendar size={14} className="text-brand-emerald" /> Enrollment: <strong className="text-brand-charcoal dark:text-white">{info.cohortName}</strong>
                </span>
                <span className="flex items-center gap-1.5">
                    <Clock size={14} className="text-amber-500" /> Revocation Deadline: <strong className="text-brand-charcoal dark:text-white">{deadlineText}</strong>
                </span>
            </div>
        </div>
    );
};
